import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import { IconButton, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { ContactDefaultCustomField, InboxContact, User } from 'src/constants/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { Contact } from 'src/modules/contacts/types';
import { Group } from 'src/modules/groups/types';
import { ChatHeaderBar } from './ChatHeaderBar.styles';
import MediaLibraryDialog from '../MediaLibraryDialog';
import useSpamContactDialog from '../SpamContactDialog';

type ChatHeaderProps = {
  contact: InboxContact | null;
  customFields: ContactDefaultCustomField[];
  groups: Group[];
  refreshContacts: (callback?: () => void) => void | Promise<void>;
  openEditContact: (data: {
    contactData: Contact;
    customFields: ContactDefaultCustomField[];
    groups: Group[];
    users: User[];
    contactGroups?: Group[];
  }) => void;
  users: User[];
  isMobile?: boolean;
  onBackToList?: () => void;
};

const ChatHeader = ({
  contact,
  groups,
  users,
  refreshContacts,
  openEditContact,
  customFields,
  isMobile,
  onBackToList,
}: ChatHeaderProps) => {
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const { SpamContactDialog, SpamButton } = useSpamContactDialog();

  if (!contact) {
    return null;
  }

  const isContactSpam = Boolean(contact.contact.isSpam?.value);
  const displayName = contact.contact.firstName || contact.contact.lastName
    ? `${contact.contact.firstName || ''} ${contact.contact.lastName || ''}`.trim()
    : contact.contact.phoneNumber;

  const handleBackTouch = (event: React.TouchEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const activeElement = document.activeElement as HTMLElement | null;
    activeElement?.blur();
    onBackToList?.();
  };

  const handleBackClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onBackToList?.();
  };

  return (
    <>
      <ChatHeaderBar>
        <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
          {isMobile && onBackToList && (
            <IconButton
              aria-label="Back to conversations"
              onTouchEnd={handleBackTouch}
              onTouchStart={event => event.stopPropagation()}
              onClick={handleBackClick}
            >
              <KeyboardBackspaceIcon />
            </IconButton>
          )}
          <Typography variant="subtitle1" noWrap>
            {!contact.contact.id ? 'To: ' : ''}{displayName}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center">
          {!!contact.contact.id && (
            <IconButton aria-label="Media library" onClick={() => setIsMediaLibraryOpen(true)}>
              <PermMediaIcon />
            </IconButton>
          )}
          {isContactSpam ? (
            <SpamButton
              selectedCompanyId={selectedCompanyId!}
              contactId={contact.contact.id}
              isSpam
              nextAction={refreshContacts}
            />
          ) : (
            <IconButton
              aria-label="Contact details"
              onClick={() => openEditContact({
                contactData: contact.contact,
                customFields,
                groups,
                users,
              })}
            >
              <InfoOutlineIcon />
            </IconButton>
          )}
        </Stack>
      </ChatHeaderBar>
      <SpamContactDialog />
      <MediaLibraryDialog
        open={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        companyId={selectedCompanyId}
        contactId={contact.contact.id}
      />
    </>
  );
};

export default ChatHeader;
